// AdminNewsBlackoutsPage — Schedule high-impact events, set lockout window
// Mirrors customer NewsBlackoutsPage but with edit/add/delete

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface NewsEvent {
  id: string;
  date: string;
  time: string;
  title: string;
  impact: 'high' | 'medium' | 'low';
  currency: 'USD' | 'EUR' | 'GBP' | 'JPY';
  pairs: string[];
  lockoutMinsBefore: number;
  lockoutMinsAfter: number;
  botBehavior: 'pause' | 'flatten' | 'warn';
  active: boolean;
}

const EVENTS: NewsEvent[] = [
  { id: 'e1', date: '2026-07-25', time: '08:30', title: 'Initial Jobless Claims',     impact: 'medium', currency: 'USD', pairs: ['ES','NQ','YM'], lockoutMinsBefore: 2, lockoutMinsAfter: 2, botBehavior: 'warn',   active: true },
  { id: 'e2', date: '2026-07-25', time: '10:00', title: 'Existing Home Sales',       impact: 'medium', currency: 'USD', pairs: ['ES'],         lockoutMinsBefore: 2, lockoutMinsAfter: 2, botBehavior: 'warn',   active: true },
  { id: 'e3', date: '2026-07-25', time: '14:00', title: 'Fed Chair Powell Speaks',   impact: 'high',   currency: 'USD', pairs: ['ES','NQ','YM','GC','CL'], lockoutMinsBefore: 2, lockoutMinsAfter: 2, botBehavior: 'pause', active: true },
  { id: 'e4', date: '2026-07-26', time: '08:30', title: 'Core Durable Goods',         impact: 'high',   currency: 'USD', pairs: ['ES','NQ'],     lockoutMinsBefore: 2, lockoutMinsAfter: 2, botBehavior: 'pause', active: true },
  { id: 'e5', date: '2026-07-26', time: '08:30', title: 'GDP (QoQ, advance)',         impact: 'high',   currency: 'USD', pairs: ['ES','NQ','YM'], lockoutMinsBefore: 2, lockoutMinsAfter: 2, botBehavior: 'pause', active: true },
  { id: 'e6', date: '2026-07-27', time: '14:00', title: 'FOMC Member Daly Speaks',   impact: 'medium', currency: 'USD', pairs: ['ES','NQ'],     lockoutMinsBefore: 2, lockoutMinsAfter: 2, botBehavior: 'warn',   active: true },
  { id: 'e7', date: '2026-07-28', time: '08:30', title: 'Core PCE (YoY)',             impact: 'high',   currency: 'USD', pairs: ['ES','NQ','GC'], lockoutMinsBefore: 2, lockoutMinsAfter: 2, botBehavior: 'pause', active: true },
];

const IMPACT_META = {
  high:   { color: '#FF3B30', dot: 'bg-[#FF3B30]' },
  medium: { color: '#FF9500', dot: 'bg-[#FF9500]' },
  low:    { color: '#34C759', dot: 'bg-[#34C759]' },
};

const BEHAVIOR_META: Record<string, { color: string; label: string; icon: string }> = {
  pause:   { color: '#FF3B30', label: 'Auto-pause',  icon: '⏸' },
  flatten: { color: '#FF9500', label: 'Auto-flatten', icon: '⏬' },
  warn:    { color: '#FFD60A', label: 'Warn only',   icon: '⚠️' },
};

export function AdminNewsBlackoutsPage() {
  const nav = useNavigate();
  const [events, setEvents] = useState(EVENTS);
  const [editing, setEditing] = useState<string | null>(null);

  function update(id: string, key: keyof NewsEvent, value: any) {
    setEvents((curr) => curr.map((e) => (e.id === id ? { ...e, [key]: value } : e)));
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-white/10 bg-[#0A0A0A]/80 backdrop-blur-xl px-2">
        <button onClick={() => nav('/admin/dashboard')} data-testid="back" className="flex h-9 w-9 items-center justify-center text-[#0A84FF] haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">News blackouts</div>
        <button onClick={() => alert('Add new event')} data-testid="add-event" className="px-3 text-[15px] text-[#0A84FF]">+ Add</button>
      </div>

      {/* Settings card */}
      <div className="mx-4 mt-3 rounded-[14px] border border-white/10 bg-white/5 p-4">
        <h2 className="text-[14px] font-bold text-white">Default lockout window</h2>
        <div className="mt-2 grid grid-cols-3 gap-2">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-white/50 mb-1">Before (min)</label>
            <input type="number" defaultValue="2" data-testid="window-before" className="w-full h-9 rounded-[8px] bg-white/5 border border-white/10 px-2.5 text-[13px] text-white tabular-nums" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-white/50 mb-1">After (min)</label>
            <input type="number" defaultValue="2" data-testid="window-after" className="w-full h-9 rounded-[8px] bg-white/5 border border-white/10 px-2.5 text-[13px] text-white tabular-nums" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-white/50 mb-1">Min impact</label>
            <select data-testid="window-impact" className="w-full h-9 rounded-[8px] bg-white/5 border border-white/10 px-2.5 text-[13px] text-white">
              <option>Low</option>
              <option>Medium</option>
              <option>High</option>
            </select>
          </div>
        </div>
        <p className="mt-2 text-[10px] text-white/50">Section 13.9 mandates a 2-min window around high-impact events. Bot behavior can be customized per event below.</p>
      </div>

      {/* Events */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-white/50">Scheduled events · {events.length}</h2>
      <div className="px-4 space-y-2">
        {events.map((e) => {
          const im = IMPACT_META[e.impact];
          const bm = BEHAVIOR_META[e.botBehavior];
          const isEdit = editing === e.id;
          return (
            <div key={e.id} data-testid={`event-${e.id}`} className="rounded-[12px] border border-white/10 bg-white/5 overflow-hidden">
              <button
                onClick={() => setEditing(isEdit ? null : e.id)}
                className="flex w-full items-center gap-3 px-3 py-2.5 text-left active:bg-white/5"
              >
                <div className="w-14 text-center">
                  <div className="text-[11px] text-white/50 tabular-nums">{e.time}</div>
                  <div className="text-[9px] font-bold uppercase mt-0.5" style={{ color: im.color }}>{e.currency}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[13px] font-semibold text-white truncate">{e.title}</span>
                  </div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <span className={['h-1.5 w-1.5 rounded-full', im.dot].join(' ')} />
                    <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: im.color }}>{e.impact}</span>
                    <span className="text-[10px] text-white/40">·</span>
                    <span className="text-[10px]" style={{ color: bm.color }}>{bm.icon} {bm.label}</span>
                    <span className="text-[10px] text-white/40">·</span>
                    <span className="text-[10px] text-white/40">{e.lockoutMinsBefore}min/{e.lockoutMinsAfter}min</span>
                  </div>
                </div>
                <div className={['rounded-full px-1.5 py-0.5 text-[8px] font-bold uppercase', e.active ? 'bg-[#34C759]/20 text-[#34C759]' : 'bg-white/10 text-white/40'].join(' ')}>{e.active ? 'ON' : 'OFF'}</div>
              </button>
              {isEdit && (
                <div className="border-t border-white/10 px-3 py-2.5 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-white/50 mb-1">Date</label>
                      <input type="date" value={e.date} onChange={(ev) => update(e.id, 'date', ev.target.value)} data-testid={`ed-date-${e.id}`} className="w-full h-8 rounded-[6px] bg-white/5 border border-white/10 px-2 text-[12px] text-white" />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-white/50 mb-1">Time</label>
                      <input type="time" value={e.time} onChange={(ev) => update(e.id, 'time', ev.target.value)} data-testid={`ed-time-${e.id}`} className="w-full h-8 rounded-[6px] bg-white/5 border border-white/10 px-2 text-[12px] text-white" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-[10px] uppercase tracking-wider text-white/50 mb-1">Title</label>
                      <input type="text" value={e.title} onChange={(ev) => update(e.id, 'title', ev.target.value)} data-testid={`ed-title-${e.id}`} className="w-full h-8 rounded-[6px] bg-white/5 border border-white/10 px-2 text-[12px] text-white" />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-white/50 mb-1">Impact</label>
                      <select value={e.impact} onChange={(ev) => update(e.id, 'impact', ev.target.value)} data-testid={`ed-impact-${e.id}`} className="w-full h-8 rounded-[6px] bg-white/5 border border-white/10 px-2 text-[12px] text-white">
                        <option>high</option><option>medium</option><option>low</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-white/50 mb-1">Bot behavior</label>
                      <select value={e.botBehavior} onChange={(ev) => update(e.id, 'botBehavior', ev.target.value)} data-testid={`ed-behavior-${e.id}`} className="w-full h-8 rounded-[6px] bg-white/5 border border-white/10 px-2 text-[12px] text-white">
                        <option>pause</option><option>flatten</option><option>warn</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-white/50 mb-1">Lockout before</label>
                      <input type="number" value={e.lockoutMinsBefore} onChange={(ev) => update(e.id, 'lockoutMinsBefore', Number(ev.target.value))} data-testid={`ed-before-${e.id}`} className="w-full h-8 rounded-[6px] bg-white/5 border border-white/10 px-2 text-[12px] text-white tabular-nums" />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-wider text-white/50 mb-1">Lockout after</label>
                      <input type="number" value={e.lockoutMinsAfter} onChange={(ev) => update(e.id, 'lockoutMinsAfter', Number(ev.target.value))} data-testid={`ed-after-${e.id}`} className="w-full h-8 rounded-[6px] bg-white/5 border border-white/10 px-2 text-[12px] text-white tabular-nums" />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-1">
                    <button onClick={() => setEditing(null)} data-testid={`save-event-${e.id}`} className="flex-1 h-8 rounded-full bg-[#0A84FF] text-white text-[12px] font-bold active:opacity-40">Save</button>
                    <button onClick={() => update(e.id, 'active', !e.active)} data-testid={`toggle-event-${e.id}`} className="flex-1 h-8 rounded-full bg-white/10 text-white text-[12px] font-semibold active:opacity-40">{e.active ? 'Disable' : 'Enable'}</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
